/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericTabs, { TabOption } from "@ui-components/Tabs/GenericTabs";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { useAuth0 } from "@auth0/auth0-react";
import { getReflectorsList } from "../../request/request";
import { createCluster } from "../request/request";
import { useAppSelector } from "@shared/redux/hooks";
import ClusterStep1 from "./ClusterStep1";
import ClusterStep2 from "./ClusterStep2";
import ClusterStep3 from "./ClusterStep3";

export interface Reflector {
  id: string;
  name: string;
  data?: { region?: string; vpc?: string };
  licenseId?: string;
  available?: boolean;
  tier?: string;
  clusterId?: number | null;
  cluster?: {
    id: number;
    name: string;
    description: string;
    enable: boolean;
  } | null;
}
export interface ClusterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

const STEPS: TabOption[] = [
  { label: "Step 1", value: "0" },
  { label: "Step 2", value: "1" },
  { label: "Step 3", value: "2" },
];

const ClusterModal: React.FC<ClusterModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [step, setStep] = useState<string>("0");
  const [clusterName, setClusterName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [reflectors, setReflectors] = useState<Reflector[]>([]);
  const [selectedReflectors, setSelectedReflectors] = useState<Reflector[]>([]);
  const [roles, setRoles] = useState<Record<string, { role?: string; address?: string; interfaceAddressId?: number; port?: number }>>({});
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'confirm'; message: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector(state => state.selection);

  // Fetch reflectors for step 2
  useEffect(() => {
    if (step === "1" && reflectors.length === 0) {
      (async () => {
        const token = await getAccessTokenSilently();
        const res = await getReflectorsList(token, tenantId);
        setReflectors(res.data);
      })();
    }
  }, [step, getAccessTokenSilently, reflectors.length]);

  // Validation
  const canProceedStep1 = clusterName.trim() !== "" && description.trim() !== "";
  
  // Check if selected reflectors are in the same region
  const selectedReflectorRegions = selectedReflectors.map(r => r.data?.region).filter(Boolean);
  const allSameRegion = selectedReflectorRegions.length === 0 || 
    selectedReflectorRegions.every(region => region === selectedReflectorRegions[0]);

  // Check if selected reflectors are on the same tier
  const selectedReflectorTiers = selectedReflectors.map(r => r.tier);
  const allSameTier = selectedReflectors.length < 2 ||
    selectedReflectorTiers.every(tier => tier === selectedReflectorTiers[0]);
  
  const canProceedStep2 = selectedReflectors.length === 2 && allSameRegion && allSameTier;

  const handleNext = () => setStep((s) => (parseInt(s) + 1).toString());
  const handleBack = () => setStep((s) => (parseInt(s) - 1).toString());

  const handleSubmit = async () => {
    if (!clusterName.trim() || !description.trim() || selectedReflectors.length === 0) {
      console.error('Missing required data:', { clusterName, description, selectedReflectors });
      setAlert({ type: 'error', message: 'Missing required data. Please fill in all fields.' });
      return;
    }

    // Prevent duplicate submissions
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (!tenantId) {
        setIsSubmitting(false);
        return;
      }
      const token = await getAccessTokenSilently();
      
      // Validate that all reflectors have required data
      const validatedReflectors = selectedReflectors.map(reflector => {
        if (!reflector || !reflector.id) {
          throw new Error(`Invalid reflector data: reflector ID is missing`);
        }
        const reflectorId = Number(reflector.id);
        if (isNaN(reflectorId)) {
          throw new Error(`Invalid reflector ID: ${reflector.id}`);
        }
        
        const roleData = roles[reflector.id];
        const reflectorPayload: any = {
          reflectorId: reflectorId,
          role: roleData?.role || 'secondary',
          port: roleData?.port || 10117
        };
        
        // Only include interfaceAddressId if it exists and is valid
        if (roleData?.interfaceAddressId !== undefined && roleData.interfaceAddressId !== null) {
          reflectorPayload.interfaceAddressId = Number(roleData.interfaceAddressId);
        }
        
        return reflectorPayload;
      });

      // Format the payload according to the Cluster interface
      const payload = {
        name: clusterName,
        description: description,
        region: selectedReflectors[0]?.data?.region || '',
        cloud: 'AWS', // Always send cloud value in uppercase
        status: 'active',
        reflectors: validatedReflectors
      };

      
      // Make the API call to create the cluster (only once)
      const response = await createCluster(token, payload, tenantId);
      
      // Only proceed if the request was successful
      if (!response || !response.data) {
        throw new Error('Cluster creation failed: No response data');
      }
      
      // Show success alert first
      setAlert({ type: 'success', message: 'Cluster created successfully!' });
      
      // Wait a bit to ensure the alert is displayed before calling onSubmit
      // This allows the user to see the success message
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Call the parent's onSubmit callback with the response data only on success
      // This should only happen once, after successful creation
      onSubmit({
        clusterName,
        description,
        selectedReflectors,
        roles,
        clusterId: response.data.id, // Pass the created cluster ID
        response: response.data
      });
      
    } catch (error: any) {
      console.error('Error creating cluster:', error);
      
      // Show error alert with more details
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to create cluster. Please try again.';
      setAlert({ type: 'error', message: errorMessage });
      
      // Don't call onSubmit on error - this prevents duplicate requests
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert(null);
    onClose(); // Close the modal when alert is dismissed
  };

  return (
    <GenericModal isOpen={isOpen} onClose={onClose} size="lg" title="Create cluster" overflowY="hidden">
      <div className="flex flex-col h-full">
        {/* Single scroll region — modal body does not scroll (no double scrollbar) */}
        <div
          className="flex-1 overflow-y-auto custom-scrollbar p-4 max-h-[70vh] w-full max-w-2xl mx-auto"
          data-testid="cluster-modal-scroll"
        >
          <GenericTabs
            tabs={STEPS}
            activeTab={step}
            onTabChange={setStep}
          />
          {step === "0" && (
            <ClusterStep1
              clusterName={clusterName}
              setClusterName={setClusterName}
              description={description}
              setDescription={setDescription}
              onNext={canProceedStep1 ? handleNext : undefined}
              onCancel={onClose}
            />
          )}
          {step === "1" && (
            <ClusterStep2
              reflectors={reflectors}
              selectedReflectors={selectedReflectors}
              setSelectedReflectors={setSelectedReflectors}
              onNext={canProceedStep2 ? handleNext : undefined}
              onBack={handleBack}
              allSameRegion={allSameRegion}
              allSameTier={allSameTier}
              selectedReflectorRegions={selectedReflectorRegions}
            />
          )}
          {step === "2" && (
            <ClusterStep3
              clusterName={clusterName}
              description={description}
              selectedReflectors={selectedReflectors}
              roles={roles}
              setRoles={setRoles}
              onBack={handleBack}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              isActive={step === "2"}
            />
          )}
        </div>

        {/* Fixed button area at bottom - only for step 2 */}
        {step === "1" && (
          <div className="p-4 bg-semantic-bg-primary">
            <div className="flex justify-end gap-2 max-w-2xl mx-auto">
              <GenericButton label="Cancel" variant="secondary" onClick={handleBack} />
              <GenericButton label="Next" variant="brand" onClick={canProceedStep2 ? handleNext : undefined} disabled={!canProceedStep2} />
            </div>
          </div>
        )}
      </div>
      
      {/* Alert for cluster creation status */}
      <GenericAlert
        isOpen={alert !== null}
        onClose={handleCloseAlert}
        onConfirm={handleCloseAlert}
        title={alert?.type === 'success' ? 'Success' : alert?.type === 'error' ? 'Error' : 'Info'}
        description={alert?.message || ''}
        variant={alert?.type}
        confirmLabel="OK"
        cancelLabel=""
      />
    </GenericModal>
  );
};

export default ClusterModal;
